import type { IndustrySlug } from '#/types';

import type {
  FacetOption,
  SearchEquitySectorGroup,
  SearchExchangeCode,
  SearchExchangeGroup,
  SearchMarketCapCategory,
} from './facetOptions';
import {
  EQUITY_INDUSTRY_GROUPS,
  SEARCH_EXCHANGE_CODES,
  SEARCH_MARKET_CAP_CATEGORIES,
} from './facetOptions';

export const ALL_EXCHANGE_CODES = SEARCH_EXCHANGE_CODES;
export const ALL_MARKET_CAP_CATEGORIES = SEARCH_MARKET_CAP_CATEGORIES;
export const EMPTY_STRING_VALUES: string[] = [];

export const ALL_SECTOR_SLUGS = EQUITY_INDUSTRY_GROUPS.map(
  (group) => group.value,
);
export const ALL_INDUSTRY_SLUGS = EQUITY_INDUSTRY_GROUPS.flatMap((group) =>
  group.industries.map((industry) => industry.value),
);

export function isUnfilteredEquitySectorSelection(
  selectedSectorSlugs: string[],
  selectedIndustrySlugs: string[],
) {
  return selectedSectorSlugs.length === 0 && selectedIndustrySlugs.length === 0;
}

export function getEffectiveEquitySectorSelection(
  selectedSectorSlugs: string[],
  selectedIndustrySlugs: string[],
) {
  if (
    isUnfilteredEquitySectorSelection(
      selectedSectorSlugs,
      selectedIndustrySlugs,
    )
  ) {
    return {
      sectorSlugs: ALL_SECTOR_SLUGS,
      industrySlugs: ALL_INDUSTRY_SLUGS,
    };
  }

  return {
    sectorSlugs: selectedSectorSlugs,
    industrySlugs: getExpandedIndustrySlugs(
      selectedSectorSlugs,
      selectedIndustrySlugs,
    ),
  };
}

export function isAllEquitySectorSelection(
  selectedSectorSlugs: string[],
  selectedIndustrySlugs: string[],
) {
  return (
    selectedSectorSlugs.length === ALL_SECTOR_SLUGS.length ||
    selectedIndustrySlugs.length === ALL_INDUSTRY_SLUGS.length
  );
}

export function getAppliedEquitySectorSelection(
  selectedSectorSlugs: string[],
  selectedIndustrySlugs: string[],
) {
  if (
    isUnfilteredEquitySectorSelection(
      selectedSectorSlugs,
      selectedIndustrySlugs,
    ) ||
    isAllEquitySectorSelection(selectedSectorSlugs, selectedIndustrySlugs)
  ) {
    return {
      equity_sector_slug: undefined,
      equity_industry_slug: undefined,
    };
  }

  return {
    equity_sector_slug: selectedSectorSlugs.length
      ? selectedSectorSlugs
      : undefined,
    equity_industry_slug: selectedIndustrySlugs.length
      ? selectedIndustrySlugs
      : undefined,
  };
}

export function getExpandedIndustrySlugs(
  selectedSectorSlugs: string[],
  selectedIndustrySlugs: string[],
) {
  const expandedIndustryValues = new Set(selectedIndustrySlugs);

  for (const group of EQUITY_INDUSTRY_GROUPS) {
    if (!selectedSectorSlugs.includes(group.value)) {
      continue;
    }

    for (const industry of group.industries) {
      expandedIndustryValues.add(industry.value);
    }
  }

  return getSelectedIndustrySlugs(expandedIndustryValues);
}

export function getSelectedIndustrySlugs(selectedIndustryValues: Set<string>) {
  return EQUITY_INDUSTRY_GROUPS.flatMap((group) =>
    group.industries
      .map((industry) => industry.value)
      .filter((slug) => selectedIndustryValues.has(slug)),
  );
}

function normalizeFacetSearchQuery(value: string) {
  return value.trim().toLowerCase();
}

export function getFilteredExchangeGroups(
  groups: SearchExchangeGroup[],
  searchQuery: string,
) {
  const normalizedQuery = normalizeFacetSearchQuery(searchQuery);

  if (!normalizedQuery) {
    return groups;
  }

  return groups.flatMap((group) => {
    const groupMatches = group.label.toLowerCase().includes(normalizedQuery);
    const exchanges = group.exchanges.filter((exchange) =>
      exchange.label.toLowerCase().includes(normalizedQuery),
    );

    if (!groupMatches && exchanges.length === 0) {
      return [];
    }

    return [
      {
        ...group,
        exchanges: groupMatches ? group.exchanges : exchanges,
      },
    ];
  });
}

export function getFilteredSectorGroups(
  groups: SearchEquitySectorGroup[],
  searchQuery: string,
) {
  const normalizedQuery = normalizeFacetSearchQuery(searchQuery);

  if (!normalizedQuery) {
    return groups;
  }

  return groups.flatMap((group) => {
    const groupMatches = group.label.toLowerCase().includes(normalizedQuery);
    const industries = group.industries.filter((industry) =>
      industry.label.toLowerCase().includes(normalizedQuery),
    );

    if (!groupMatches && industries.length === 0) {
      return [];
    }

    return [
      {
        ...group,
        industries: groupMatches ? group.industries : industries,
      },
    ];
  });
}

export function getEquitySectorTriggerLabel({
  selectedSectorSlugs,
  selectedIndustrySlugs,
}: {
  selectedSectorSlugs: string[];
  selectedIndustrySlugs: string[];
}) {
  const selectedCount =
    selectedIndustrySlugs.length || selectedSectorSlugs.length;

  if (selectedCount === 0) {
    return 'Sector: Any';
  }

  const fullGroupFromIndustries = EQUITY_INDUSTRY_GROUPS.find((group) => {
    if (selectedIndustrySlugs.length !== group.industries.length) {
      return false;
    }

    return group.industries.every((industry) =>
      selectedIndustrySlugs.includes(industry.value),
    );
  });

  if (fullGroupFromIndustries) {
    return `Sector: ${fullGroupFromIndustries.label}`;
  }

  if (selectedIndustrySlugs.length === 1) {
    for (const group of EQUITY_INDUSTRY_GROUPS) {
      const industry = group.industries.find(
        (option) => option.value === selectedIndustrySlugs[0],
      );

      if (industry) {
        return `Sector: ${industry.label}`;
      }
    }
  }

  if (selectedSectorSlugs.length === 1 && selectedIndustrySlugs.length === 0) {
    const group = EQUITY_INDUSTRY_GROUPS.find(
      (option) => option.value === selectedSectorSlugs[0],
    );

    if (group) {
      return `Sector: ${group.label}`;
    }
  }

  return `Sector: ${selectedCount} selected`;
}

export function getEquitySectorSelectedCount({
  selectedSectorSlugs,
  selectedIndustrySlugs,
}: {
  selectedSectorSlugs: string[];
  selectedIndustrySlugs: string[];
}) {
  return selectedIndustrySlugs.length || selectedSectorSlugs.length;
}

function getFacetOptionLabel(options: FacetOption[], value: string) {
  return options.find((option) => option.value === value)?.label ?? value;
}

export function getMultiSelectTriggerLabel({
  label,
  selectedValues,
  options,
}: {
  label: string;
  selectedValues: string[];
  options: FacetOption[];
}) {
  if (!selectedValues.length) {
    return `${label}: Any`;
  }

  if (selectedValues.length === 1) {
    return `${label}: ${getFacetOptionLabel(options, selectedValues[0] ?? '')}`;
  }

  return `${label}: ${selectedValues.length} selected`;
}

export function getToggledFacetValues(
  selectedValues: string[],
  nextValue: string,
  options: FacetOption[],
) {
  const nextSelectedValues = new Set(selectedValues);

  if (nextSelectedValues.has(nextValue)) {
    nextSelectedValues.delete(nextValue);
  } else {
    nextSelectedValues.add(nextValue);
  }

  return options
    .filter((option) => option.value && nextSelectedValues.has(option.value))
    .map((option) => option.value);
}

export function getExchangeTriggerLabel(
  selectedExchangeCodes: SearchExchangeCode[],
) {
  const selectedCount = selectedExchangeCodes.length;

  if (selectedCount === 0 || selectedCount === ALL_EXCHANGE_CODES.length) {
    return 'Exchange';
  }

  return `Exchange ${selectedCount}`;
}

export function getMarketCapTriggerLabel(
  selectedMarketCapCategories: SearchMarketCapCategory[],
) {
  const selectedCount = selectedMarketCapCategories.length;

  if (
    selectedCount === 0 ||
    selectedCount === ALL_MARKET_CAP_CATEGORIES.length
  ) {
    return 'Market Cap';
  }

  return `Market Cap ${selectedCount}`;
}

export function getToggledMarketCapValues(
  selectedMarketCapCategories: SearchMarketCapCategory[],
  nextMarketCapCategory: SearchMarketCapCategory,
) {
  const nextSelectedValues = new Set(selectedMarketCapCategories);

  if (nextSelectedValues.has(nextMarketCapCategory)) {
    nextSelectedValues.delete(nextMarketCapCategory);
  } else {
    nextSelectedValues.add(nextMarketCapCategory);
  }

  return ALL_MARKET_CAP_CATEGORIES.filter((category) =>
    nextSelectedValues.has(category),
  );
}

export function getGroupCheckedState(
  group: SearchExchangeGroup,
  selectedValues: Set<SearchExchangeCode>,
) {
  const selectedCount = group.exchanges.filter((exchange) =>
    selectedValues.has(exchange.value),
  ).length;

  return {
    checked: selectedCount === group.exchanges.length,
    indeterminate: selectedCount > 0 && selectedCount < group.exchanges.length,
  };
}

export function getToggledExchangeGroupValues(
  group: SearchExchangeGroup,
  selectedExchangeCodes: SearchExchangeCode[],
) {
  const nextSelectedValues = new Set(selectedExchangeCodes);
  const groupState = getGroupCheckedState(group, nextSelectedValues);

  for (const exchange of group.exchanges) {
    if (groupState.checked) {
      nextSelectedValues.delete(exchange.value);
    } else {
      nextSelectedValues.add(exchange.value);
    }
  }

  return ALL_EXCHANGE_CODES.filter((code) => nextSelectedValues.has(code));
}

export function getToggledExchangeValues(
  selectedExchangeCodes: SearchExchangeCode[],
  nextExchangeCode: SearchExchangeCode,
) {
  const nextSelectedValues = new Set(selectedExchangeCodes);

  if (nextSelectedValues.has(nextExchangeCode)) {
    nextSelectedValues.delete(nextExchangeCode);
  } else {
    nextSelectedValues.add(nextExchangeCode);
  }

  return ALL_EXCHANGE_CODES.filter((code) => nextSelectedValues.has(code));
}

export function getSectorGroupCheckedState(
  group: SearchEquitySectorGroup,
  selectedSectorValues: Set<string>,
  selectedIndustryValues: Set<string>,
) {
  if (selectedSectorValues.has(group.value)) {
    return { checked: true, indeterminate: false };
  }

  const selectedCount = group.industries.filter((industry) =>
    selectedIndustryValues.has(industry.value),
  ).length;

  return {
    checked: selectedCount === group.industries.length,
    indeterminate: selectedCount > 0 && selectedCount < group.industries.length,
  };
}

export function toggleSectorGroupSelection({
  group,
  selectedSectorSlugs,
  selectedIndustrySlugs,
}: {
  group: SearchEquitySectorGroup;
  selectedSectorSlugs: string[];
  selectedIndustrySlugs: string[];
}) {
  const nextSectorValues = new Set(selectedSectorSlugs);
  const nextIndustryValues = new Set(
    getExpandedIndustrySlugs(selectedSectorSlugs, selectedIndustrySlugs),
  );
  const groupState = getSectorGroupCheckedState(
    group,
    nextSectorValues,
    nextIndustryValues,
  );

  if (groupState.checked) {
    nextSectorValues.delete(group.value);
    for (const industry of group.industries) {
      nextIndustryValues.delete(industry.value);
    }
  } else {
    nextSectorValues.add(group.value);
    for (const industry of group.industries) {
      nextIndustryValues.add(industry.value);
    }
  }

  return {
    sectorSlugs: ALL_SECTOR_SLUGS.filter((slug) => nextSectorValues.has(slug)),
    industrySlugs: getSelectedIndustrySlugs(nextIndustryValues),
  };
}

export function toggleIndustrySelection({
  group,
  industrySlug,
  selectedSectorSlugs,
  selectedIndustrySlugs,
}: {
  group: SearchEquitySectorGroup;
  industrySlug: IndustrySlug;
  selectedSectorSlugs: string[];
  selectedIndustrySlugs: string[];
}) {
  const nextSectorValues = new Set(selectedSectorSlugs);
  const nextIndustryValues = new Set(
    getExpandedIndustrySlugs(selectedSectorSlugs, selectedIndustrySlugs),
  );

  nextSectorValues.delete(group.value);

  if (nextIndustryValues.has(industrySlug)) {
    nextIndustryValues.delete(industrySlug);
  } else {
    nextIndustryValues.add(industrySlug);
  }

  return {
    sectorSlugs: ALL_SECTOR_SLUGS.filter((slug) => nextSectorValues.has(slug)),
    industrySlugs: getSelectedIndustrySlugs(nextIndustryValues),
  };
}
